import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { config } from "../../../../config/envs";
import { generateTraceId } from "../../../helpers";
import useFeatureFlag from "../../../hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "../../../constants/FeatureFlag";
import { CourtesySubscriptionsAPI } from "../../../api/payments/courtesySubscriptions/api";
import { toCourtesyJusSubInterval } from "../../../constants/courtesy";

const toCourtesyDays = data => {
  if (data.activeDays) {
    return data.activeDays;
  }
  if (data.activeMonths) {
    return data.activeMonths * 30;
  }
  return undefined;
};

export class CourtesyBlockedError extends Error {
  constructor(message) {
    super(message);
    this.name = "CourtesyBlockedError";
  }
}

// O provider é definido só pelo tipo do cliente: jussub (com external_id) usa o
// fluxo jussub, jusbill usa o fluxo jusbill, independente da feature flag.
// A flag é validada dentro do fluxo jussub: com ela desligada a cortesia é
// bloqueada, nunca redirecionada para o fluxo jusbill.
export function useFinanceiroCourtesySubmit(
  clienteId,
  { isJussubClient = false, isCustomerLoaded = false } = {}
) {
  const canAccess = useFeatureFlag(FEATURE_FLAGS.KILL_SWITCH.ADMIN_COURTESY_JUSSUB);

  const createCourtesyJusSub = data => {
    if (!canAccess.flagResolved) {
      return Promise.reject(
        new CourtesyBlockedError("Carregando configurações, tente novamente.")
      );
    }
    if (!canAccess.isFeatureFlagEnable) {
      return Promise.reject(
        new CourtesyBlockedError(
          "Criação de cortesia para clientes JusSub não está habilitada."
        )
      );
    }

    return CourtesySubscriptionsAPI.createForExistingClient(clienteId, {
      provider: "jussub",
      planId: data.planId,
      courtesyDays: toCourtesyJusSubInterval(toCourtesyDays(data)),
      courtesyReason: data.ReleaseCategory
    });
  };

  const createCourtesyJusbill = data => {
    const payload = {
      provider: "jusbill",
      ReleaseCategory: data.ReleaseCategory,
      transactionId: uuidv4()
    };
    if (data.activeMonths) {
      payload.activeMonths = data.activeMonths;
    }
    if (data.activeDays) {
      payload.activeDays = data.activeDays;
    }
    if (data.planId) {
      payload.planId = data.planId;
    }

    return axios.post(
      config.app.adminBaseUrl + `/courtesy-subscriptions/clients/${clienteId}`,
      payload,
      { headers: { "x-trace-id": generateTraceId() } }
    );
  };

  const createCourtesy = data => {
    // Sem o customer carregado não dá pra saber o provider: não arrisca o fluxo errado.
    if (!isCustomerLoaded) {
      return Promise.reject(
        new CourtesyBlockedError(
          "Dados do cliente ainda não carregados, tente novamente."
        )
      );
    }
    return isJussubClient ? createCourtesyJusSub(data) : createCourtesyJusbill(data);
  };

  return { createCourtesy, useJusSub: isJussubClient };
}
